'use client';

import { useContext, useState, useCallback } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from 'src/contexts';
import IconSvg from 'src/components/common/ui/IconSvg';

// ---------------- Navigation Config ----------------
type NavigationItem = {
  name: string;
  path: string;
  icon?: React.ComponentType<{ ariaHidden?: boolean; className?: string }>;
  children?: NavigationItem[];
};

const navigation: NavigationItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: () => <IconSvg icon="dashboard" /> },
  {
    name: 'Your Campaign',
    path: '/dashboard/your-campaign',
    icon: () => <IconSvg icon="yourCampaign" />,
    children: [
      { name: 'Your Audience', path: '/dashboard/your-audience' },
      { name: 'Your Creatives', path: '/dashboard/your-creatives' },
    ],
  },
  {
    name: 'Creative Developers',
    path: '/dashboard/creative-developers',
    icon: () => <IconSvg icon="creativeDeveloper" />,
  },
  {
    name: 'Your Account',
    path: '/dashboard/your-account',
    icon: () => <IconSvg icon="yourAccount" />,
  },
  {
    name: 'Account Setup',
    path: '/dashboard/account-setup',
    icon: () => <IconSvg icon="accountSetup" />,
  },
  {
    name: 'Website & CRM Linking',
    path: '/dashboard/website-crm-linking',
    icon: () => <IconSvg icon="crmLinking" />,
  },
];

const userNavigation = [
  { name: 'Your profile', href: '/dashboard/your-account' },
  { name: 'Log out', href: '#' },
];

const notificationItems = [
  { name: 'Notification 1', href: '#' },
  { name: 'Notification 2', href: '#' },
  { name: 'Notification 3', href: '#' },
];

const classNames = (...classes: string[]) => classes.filter(Boolean).join(' ');

// ---------------- Sidebar Component ----------------
const Sidebar = ({
  isOpen,
  onClose,
  navigate,
  location,
  customLogoutHandler,
}: {
  isOpen: boolean;
  onClose: () => void;
  navigate: (path: string) => void;
  location: { pathname: string };
  customLogoutHandler: () => void;
}) => {
  const renderNavItem = (item: NavigationItem) => {
    const isActive = location.pathname.startsWith(item.path);

    return (
      <li key={item.name}>
        <button
          onClick={() => navigate(item.path)}
          className={classNames(
            isActive
              ? 'bg-[#F7F5FF] text-black font-semibold'
              : 'text-black font-normal hover:bg-[#F7F5FF] hover:text-black hover:font-semibold',
            'group flex gap-x-3 rounded-md font-inter px-3 py-2 text-sm w-full text-left',
          )}
        >
          {item.icon && (
            <item.icon
              aria-hidden="true"
              className={classNames(
                isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                'size-6 shrink-0',
              )}
            />
          )}
          {item.name}
        </button>

        {/* Render children (subpages) */}
        {item.children && (
          <ul className="ml-9 mt-1 space-y-1">
            {item.children.map(sub => {
              const isSubActive = location.pathname === sub.path;
              return (
                <li key={sub.name}>
                  <button
                    onClick={() => navigate(sub.path)}
                    className={classNames(
                      isSubActive
                        ? 'bg-[#F7F5FF] text-black font-semibold'
                        : 'text-black font-normal hover:bg-[#F7F5FF] hover:text-black hover:font-semibold',
                      'block rounded-md px-3 py-2 text-sm w-full text-left',
                    )}
                  >
                    {sub.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50 lg:hidden">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 flex">
        <DialogPanel
          transition
          className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
        >
          <TransitionChild>
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
              <button type="button" onClick={onClose} className="-m-2.5 p-2.5">
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon aria-hidden="true" className="size-6 text-white" />
              </button>
            </div>
          </TransitionChild>
          <div className="flex grow flex-col gap-y-5 border rounded-2xl border-[#E5E7E7] bg-white p-6">
            <div className="flex h-16 shrink-0 items-center">
              <IconSvg
                icon="appLogo"
                alt="Your Company"
                src="/assets/images/logo.svg"
                className="mx-auto cursor-pointer"
                onClick={() => navigate('/dashboard')}
              />
            </div>
            <div
              className="mx-auto w-[200px] h-[2px] rounded-sm"
              style={{ background: 'rgba(168, 168, 168, 0.10)' }}
            />
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col -mx-2 space-y-1">
                {navigation.map(item => renderNavItem(item))}
              </ul>
              <div
                className="mx-auto w-[200px] h-[2px] rounded-sm mt-6"
                style={{ background: 'rgba(168, 168, 168, 0.10)' }}
              />
              <li className="mt-auto list-none">
                <p
                  className="group flex gap-x-3 rounded-md px-2 text-xs font-normal text-black cursor-pointer"
                  onClick={customLogoutHandler}
                >
                  <IconSvg icon="logout" />
                  Logout
                </p>
              </li>
            </nav>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// ---------------- Main Layout ----------------
export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { profile, customLogoutHandler } = useContext(AuthContext);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      setSidebarOpen(false);
    },
    [navigate],
  );

  const handleProfileClick = useCallback(
    (close: () => void) => {
      navigate('/dashboard/your-account');
      close();
    },
    [navigate],
  );

  const handleLogoutClick = useCallback(() => {
    customLogoutHandler();
  }, [customLogoutHandler]);

  // Sidebar render function reuse for desktop
  const renderNavItem = (item: NavigationItem) => {
    const isActive = location.pathname.startsWith(item.path);

    return (
      <li key={item.name}>
        <button
          onClick={() => navigate(item.path)}
          className={classNames(
            isActive
              ? 'bg-[#F7F5FF] text-black font-semibold'
              : 'text-black font-normal hover:bg-[#F7F5FF] hover:text-black hover:font-semibold',
            'group flex gap-x-3 rounded-md font-inter px-3 py-2 text-sm w-full text-left',
          )}
        >
          {item.icon && (
            <item.icon
              aria-hidden="true"
              className={classNames(
                isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                'size-6 shrink-0',
              )}
            />
          )}
          {item.name}
        </button>
        {item.children && (
          <ul className="ml-9 mt-1 space-y-1">
            {item.children.map(sub => {
              const isSubActive = location.pathname === sub.path;
              return (
                <li key={sub.name}>
                  <button
                    onClick={() => navigate(sub.path)}
                    className={classNames(
                      isSubActive
                        ? 'bg-[#F7F5FF] text-black font-semibold'
                        : 'text-black font-normal hover:bg-[#F7F5FF] hover:text-black hover:font-semibold',
                      'block rounded-md px-3 py-2 text-sm w-full text-left',
                    )}
                  >
                    {sub.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div>
      {/* Mobile Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        navigate={handleNavigate}
        location={location}
        customLogoutHandler={customLogoutHandler}
      />

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 p-6 lg:flex lg:w-80 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 border rounded-2xl border-[#E5E7E7] bg-white p-6">
          <div className="flex h-16 shrink-0 items-center">
            <IconSvg
              icon="appLogo"
              alt="Your Company"
              src="/assets/images/logo.svg"
              className="mx-auto cursor-pointer"
              onClick={() => navigate('/dashboard')}
            />
          </div>
          <div
            className="mx-auto w-[200px] h-[2px] rounded-sm"
            style={{ background: 'rgba(168, 168, 168, 0.10)' }}
          />
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col -mx-2 space-y-1">
              {navigation.map(item => renderNavItem(item))}
            </ul>
            <div
              className="mx-auto w-[200px] h-[2px] rounded-sm mt-6"
              style={{ background: 'rgba(168, 168, 168, 0.10)' }}
            />
            <li className="mt-auto list-none">
              <p
                className="group flex gap-x-3 rounded-md px-2 text-xs font-normal text-black cursor-pointer"
                onClick={customLogoutHandler}
              >
                <IconSvg icon="logout" />
                Logout
              </p>
            </li>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-72 lg:p-6">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 bg-white px-4 sm:gap-x-6 sm:px-6 lg:px-8">
          <IconSvg
            icon="appLogo"
            alt="Your Company"
            src="/assets/images/logo.svg"
            className="lg:mx-auto h-8 cursor-pointer lg:hidden block"
            onClick={() => navigate('/dashboard')}
          />

          <p className="lg:block hidden font-inter text-black text-[22px] font-bold flex items-center gap-3">
            <span>
              Welcome {profile?.firstName} {profile?.lastName}!{' '}
            </span>
            <IconSvg icon="hand" className="inline-block w-6 h-6 flex-shrink-0" />
          </p>

          <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <form action="#" method="GET" className="grid flex-1 grid-cols-1">
                <input
                  name="search"
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  className="col-start-1 row-start-1 block size-full bg-white py-2 px-4 text-sm font-inter font-normal text-[#A7AAA9] border border-[#E5E7E7] rounded-[10px] outline-none placeholder:text-gray-400 sm:text-sm/6"
                />
              </form>

              {/* Notifications */}
              <Menu as="div" className="relative">
                <MenuButton className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <IconSvg icon="notification" className="size-6" />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  {notificationItems.map(item => (
                    <MenuItem key={item.name}>
                      <a
                        href={item.href}
                        onClick={e => e.preventDefault()}
                        className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                      >
                        {item.name}
                      </a>
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>

              {/* Profile Menu */}
              <Menu as="div" className="relative">
                <MenuButton className="-m-1.5 flex items-center p-1.5 gap-5">
                  {profile?.firstName && profile?.lastName ? (
                    <div className="flex w-[44px] h-[44px] items-center justify-center rounded-full bg-gray-300 text-gray-800 text-lg font-semibold">
                      {`${(profile?.firstName?.[0] ?? '').toUpperCase()}${(
                        profile?.lastName?.[0] ?? ''
                      ).toUpperCase()}`}
                    </div>
                  ) : (
                    <div className="flex w-[44px] h-[44px] items-center justify-center rounded-full bg-gray-300 text-gray-800 text-lg font-semibold"></div>
                  )}
                  <span className="hidden lg:flex lg:items-center">
                    <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
                  </span>
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  {userNavigation.map(item => (
                    <MenuItem key={item.name}>
                      {({ close }) => (
                        <a
                          href={item.href}
                          onClick={e => {
                            e.preventDefault();
                            if (item.name === 'Your profile') {
                              handleProfileClick(close);
                            } else if (item.name === 'Log out') {
                              handleLogoutClick();
                            }
                          }}
                          className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                        >
                          {item.name}
                        </a>
                      )}
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center space-x-4 lg:p-0 p-4 ">
          <IconSvg icon="sideMenu" onClick={() => setSidebarOpen(true)} className="cursor-pointer" />
          <p className="font-inter text-black text-[22px] font-bold">
            Welcome {profile?.firstName} {profile?.lastName}!
          </p>
        </div>

        {/* Main Content */}
        <main className="lg:p-8 p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
